import React, { useEffect, useState, PropsWithChildren } from 'react';
import useWalkme from 'src/hooks/useWalkme';
import { PopupData } from 'src/types/popup.model';
import { WalkmeAdobeTags, WalkmeName, WalkmeStep } from 'src/types/walkme.model';
import Popup, { PopupProps } from '../../molecules/popup/Popup';
import { trackClick, trackImpression } from 'src/hooks/useAdobeTags';
import { useLocation } from 'react-router-dom';
import { useUserAttributes } from 'src/hooks/queries/users';
import styles from './Walkme.module.scss';

export interface WalkmePopupWrapperProps {
    walkmeName: WalkmeName;
    stepIndex: number;
    overrides?: { [key: string]: string };
}

function WalkmePopupWrapper({
    walkmeName,
    stepIndex,
    overrides,
    children
}: PropsWithChildren<WalkmePopupWrapperProps>): JSX.Element {
    const { isEnabled, isNewUser, isLoading, incrementStepIndex, markAsDone, getStepData } = useWalkme(walkmeName);
    const [stepIndexMatches, setStepIndexMatches] = useState<boolean>(false);
    const [popupData, setPopupData] = useState<PopupData>(null);
    const { pathname } = useLocation();
    const [adobeTagData, setAdobeTagData] = useState<WalkmeAdobeTags>(null);
    const [impressionCount, setImpressionCount] = useState<number>(0);
    const { data: userAttributesData = {} } = useUserAttributes();
    const { transactionClassificationStartupStep = 0 } = userAttributesData;

    const handleNext = async (): Promise<void> => {
        await incrementStepIndex(transactionClassificationStartupStep);
        if (adobeTagData) trackClick(pathname, adobeTagData.next, adobeTagData.widgetName);
    };

    const handleClose = async (): Promise<void> => {
        await markAsDone();
        if (adobeTagData) trackClick(pathname, adobeTagData.close, adobeTagData.widgetName);
    };

    useEffect(() => {
        setStepIndexMatches(transactionClassificationStartupStep === stepIndex);
        const newData: WalkmeStep = getStepData(transactionClassificationStartupStep);
        setPopupData(newData?.data as PopupData);
        setAdobeTagData(newData?.adobeTags || null);
    }, [walkmeName, stepIndex, isEnabled, isNewUser, isLoading, getStepData, transactionClassificationStartupStep]);

    if (isEnabled && !isNewUser && !isLoading && stepIndexMatches && popupData) {
        const props: PopupProps = {
            ...popupData,
            handleNext,
            handleClose,
            id: `${walkmeName}-${stepIndex}`,
            leftOverride: overrides?.left,
            topOverride: overrides?.top,
            className: styles.walkmeIn
        };
        if (adobeTagData && impressionCount === 0) {
            trackImpression(pathname, adobeTagData.impression, adobeTagData.widgetName);
            setImpressionCount(impressionCount + 1);
        }
        return <Popup {...props}>{children}</Popup>;
    }
    return <>{children}</>;
}

export default WalkmePopupWrapper;
